import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth.jsx';
import { BrandProvider } from './hooks/useBrand.jsx';
import { PublicLayout } from './components/Layout.jsx';
import { RequireAuth } from './components/RequireAuth.jsx';
import { Seo } from './components/Seo.jsx';
import { Spinner } from './components/ui.jsx';

import Login from './routes/Login.jsx';
import Signup from './routes/Signup.jsx';
import ForgotPassword from './routes/ForgotPassword.jsx';
import ResetPassword from './routes/ResetPassword.jsx';
import VerifyEmail from './routes/VerifyEmail.jsx';
import Home from './routes/Home.jsx';
import { ForCreators, ForBrands } from './routes/Landing.jsx';
import Transparency from './routes/Transparency.jsx';
import { HelpIndex, HelpArticle } from './routes/Help.jsx';
import { BlogIndex, BlogPost } from './routes/Blog.jsx';
import ForAgents from './routes/ForAgents.jsx';
import Boost from './routes/Boost.jsx';
import Terms from './routes/Terms.jsx';
import Privacy from './routes/Privacy.jsx';
import EngagementRateCalculator from './routes/tools/EngagementRateCalculator.jsx';
import MediaKitGenerator from './routes/tools/MediaKitGenerator.jsx';
import EarningsCalculator from './routes/tools/EarningsCalculator.jsx';
import ProgramCalculator from './routes/tools/ProgramCalculator.jsx';
import ProgramsDirectory from './routes/ProgramsDirectory.jsx';
import Apply from './routes/Apply.jsx';
import ProgramDetail from './routes/ProgramDetail.jsx';
import CreatorsDirectory from './routes/CreatorsDirectory.jsx';
import BrandDetail from './routes/BrandDetail.jsx';
import CreatorDetail from './routes/CreatorDetail.jsx';
import CreatorProfileEditor from './routes/dashboard/CreatorProfileEditor.jsx';
import CreatorOverview from './routes/dashboard/CreatorOverview.jsx';
import CreatorEarnings from './routes/dashboard/CreatorEarnings.jsx';
import CreatorPayouts from './routes/dashboard/CreatorPayouts.jsx';
import BrandOverview from './routes/dashboard/BrandOverview.jsx';
import BrandGifts from './routes/dashboard/BrandGifts.jsx';
import BrandPosts from './routes/dashboard/BrandPosts.jsx';
import BrandSettle from './routes/dashboard/BrandSettle.jsx';
import BrandBilling from './routes/dashboard/BrandBilling.jsx';
import { BrandInvite, BrandFindCreators } from './routes/dashboard/BrandInvite.jsx';
import AddRole from './routes/dashboard/AddRole.jsx';
import ApiKeys from './routes/dashboard/ApiKeys.jsx';
import Account from './routes/dashboard/Account.jsx';
import FinishSignup from './routes/FinishSignup.jsx';
import Pricing from './routes/dashboard/Pricing.jsx';
import Applications from './routes/dashboard/Applications.jsx';
import Pitches from './routes/dashboard/Pitches.jsx';
import PitchNew from './routes/dashboard/PitchNew.jsx';
import PartnershipsPage from './routes/dashboard/Partnerships.jsx';
import AdminHealth from './routes/admin/Health.jsx';
import AdminDiagrams from './routes/admin/Diagrams.jsx';
import BrandProfileEditor from './routes/dashboard/BrandProfileEditor.jsx';
import BrandPrograms from './routes/dashboard/BrandPrograms.jsx';
import ProgramEditor from './routes/dashboard/ProgramEditor.jsx';
import ProgramEvents from './routes/dashboard/ProgramEvents.jsx';
import ProgramCompetition from './routes/dashboard/ProgramCompetition.jsx';
import ProgramPublish from './routes/dashboard/ProgramPublish.jsx';
import CreatorCompetitions from './routes/dashboard/CreatorCompetitions.jsx';
import BrandWizard from './routes/start/BrandWizard.jsx';
import CreatorStart from './routes/start/CreatorStart.jsx';

/**
 * Route table.
 *
 * Public routes come first and there are deliberately many of them: everything
 * a visitor needs to evaluate the marketplace — full program terms, creator
 * rates, both directories — is reachable with no account. Only dashboards and
 * the act of applying sit behind auth.
 */
export default function App() {
  return (
    <AuthProvider>
      <BrandProvider>
      <Routes>
        {/* Auth — no chrome, full-page forms. */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup/finish" element={<FinishSignup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Guided setup. The brand wizard is chrome-free like the dashboards. */}
        <Route path="/start/brand" element={<BrandStart />} />

        {/* Dashboards — the only authenticated surface. */}
        <Route path="/dashboard" element={<DashboardIndex />} />
        <Route
          path="/dashboard/keys"
          element={
            <RequireAuth>
              <ApiKeys />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/account"
          element={
            <RequireAuth>
              <Account />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/pricing"
          element={
            <RequireAuth>
              <Pricing />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/add-role"
          element={
            <RequireAuth>
              <AddRole />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/creators"
          element={
            <RequireAuth role="brand">
              <BrandFindCreators />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/invite/:slug"
          element={
            <RequireAuth role="brand">
              <BrandInvite />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/creator"
          element={
            <RequireAuth role="creator">
              <CreatorOverview />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/creator/earnings"
          element={
            <RequireAuth role="creator">
              <CreatorEarnings />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/creator/payouts"
          element={
            <RequireAuth role="creator">
              <CreatorPayouts />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/creator/applications"
          element={
            <RequireAuth role="creator">
              <Applications side="creator" />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/creator/pitches"
          element={
            <RequireAuth role="creator">
              <Pitches side="creator" />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/creator/pitches/new"
          element={
            <RequireAuth role="creator">
              <PitchNew />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/creator/partnerships"
          element={
            <RequireAuth role="creator">
              <PartnershipsPage side="creator" />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand"
          element={
            <RequireAuth role="brand">
              <BrandOverview />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/billing"
          element={
            <RequireAuth role="brand">
              <BrandBilling />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/settle"
          element={
            <RequireAuth role="brand">
              <BrandSettle />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/applications"
          element={
            <RequireAuth role="brand">
              <Applications side="brand" />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/pitches"
          element={
            <RequireAuth role="brand">
              <Pitches side="brand" />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/posts"
          element={
            <RequireAuth role="brand">
              <BrandPosts />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/partnerships"
          element={
            <RequireAuth role="brand">
              <PartnershipsPage side="brand" />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/gifts"
          element={
            <RequireAuth role="brand">
              <BrandGifts />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/creator/profile"
          element={
            <RequireAuth role="creator">
              <CreatorProfileEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/profile"
          element={
            <RequireAuth role="brand">
              <BrandProfileEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/new"
          element={
            <RequireAuth role="brand">
              <BrandProfileEditor createNew />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/onboarding"
          element={<Navigate to="/dashboard/brand/profile" replace />}
        />
        <Route
          path="/dashboard/brand/programs"
          element={
            <RequireAuth role="brand">
              <BrandPrograms />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/programs/new"
          element={
            <RequireAuth role="brand">
              <ProgramEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/programs/:id"
          element={
            <RequireAuth role="brand">
              <ProgramEditor />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/programs/:id/actions"
          element={
            <RequireAuth role="brand">
              <ProgramEvents />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/programs/:id/publish"
          element={
            <RequireAuth role="brand">
              <ProgramPublish />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/brand/programs/:id/competition"
          element={
            <RequireAuth role="brand">
              <ProgramCompetition />
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard/creator/competitions"
          element={
            <RequireAuth role="creator">
              <CreatorCompetitions />
            </RequireAuth>
          }
        />

        {/* Everything else shares the public shell. */}
        <Route
          path="*"
          element={
            <PublicLayout>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/for-creators" element={<ForCreators />} />
                <Route path="/for-brands" element={<ForBrands />} />
                <Route path="/for-agents" element={<ForAgents />} />
                <Route path="/transparency" element={<Transparency />} />
                <Route path="/help" element={<HelpIndex />} />
                <Route path="/help/:slug" element={<HelpArticle />} />
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/boost" element={<Boost />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route
                  path="/tools/engagement-rate-calculator"
                  element={<EngagementRateCalculator />}
                />
                <Route path="/tools/media-kit-generator" element={<MediaKitGenerator />} />
                <Route
                  path="/tools/affiliate-earnings-calculator"
                  element={<EarningsCalculator />}
                />
                <Route path="/tools/affiliate-program-calculator" element={<ProgramCalculator />} />
                <Route path="/programs" element={<ProgramsDirectory />} />
                <Route path="/programs/:slug" element={<ProgramDetail />} />
                {/* Open to logged-out visitors: the form creates the account inline. */}
                <Route path="/programs/:slug/apply" element={<Apply />} />
                <Route path="/start/creator" element={<CreatorStart />} />
                <Route path="/creators" element={<CreatorsDirectory />} />
                <Route path="/creators/:slug" element={<CreatorDetail />} />
                <Route path="/brands/:slug" element={<BrandDetail />} />
                <Route
                  path="/admin/health"
                  element={
                    <RequireAuth role="admin">
                      <AdminHealth />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/admin/diagrams/:section?"
                  element={
                    <RequireAuth role="admin">
                      <AdminDiagrams />
                    </RequireAuth>
                  }
                />
                <Route path="/admin" element={<Navigate to="/admin/health" replace />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PublicLayout>
          }
        />
      </Routes>
      </BrandProvider>
    </AuthProvider>
  );
}

/** Signed-out visitors go to sign up and come straight back; everyone else needs the brand role. */
function BrandStart() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-ink-faint">
        <Spinner />
      </div>
    );
  }
  if (!user) {
    const next = encodeURIComponent(`/start/brand${location.search}`);
    return <Navigate to={`/signup?role=brand&next=${next}`} replace />;
  }
  return (
    <RequireAuth role="brand">
      <BrandWizard />
    </RequireAuth>
  );
}

/** Sends a user to whichever dashboard they actually hold a role for. */
function DashboardIndex() {
  const { user, loading, isBrand } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-ink-faint">
        <Spinner />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={isBrand ? '/dashboard/brand' : '/dashboard/creator'} replace />;
}

function NotFound() {
  return (
    <>
      <Seo title="Page not found" noindex />
      <div className="mx-auto max-w-xl px-6 py-32 text-center">
        <p className="text-2xs font-semibold uppercase tracking-widest text-ink-faint">404</p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight">We couldn’t find that page.</h1>
        <p className="mt-3 text-ink-muted">
          Try the{' '}
          <Link to="/programs" className="text-accent hover:underline">
            programs directory
          </Link>{' '}
          or{' '}
          <Link to="/creators" className="text-accent hover:underline">
            browse creators
          </Link>
          .
        </p>
      </div>
    </>
  );
}
